import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Select from 'react-select'; // Keep Select for country/city dropdowns
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { UploadCloud, Loader2, Calendar } from 'lucide-react'; // Keep Lucide icons
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useFlatpickrUserType } from '../../hooks/useFlatpickrUserType';


const CompleteProfile = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { userType } = useParams();
  const location = useLocation();
  const { oauthLogin } = useAuth();
  const mounted = useRef(true);
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
  const [isRTL, setIsRTL] = useState(typeof i18n.dir === 'function' ? i18n.dir() === 'rtl' : i18n.language === 'ar');

  useEffect(() => {
    const handleLanguageChange = (lng) => {
      const isDirectionRTL = typeof i18n.dir === 'function' ? i18n.dir(lng) === 'rtl' : lng === 'ar';
      setIsRTL(isDirectionRTL);
      document.documentElement.dir = isDirectionRTL ? 'rtl' : 'ltr';
      document.documentElement.lang = lng;
    };

    handleLanguageChange(i18n.language);
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // State for user data
  const [user, setUser] = useState(null);
  const [isValidating, setIsValidating] = useState(true);
  const [countries, setCountries] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]); // For react-select
  const [cities, setCities] = useState([]);
  const [cityOptions, setCityOptions] = useState([]); // For react-select
  const [specialities, setSpecialities] = useState([]);
  const [specialityOptions, setSpecialityOptions] = useState([]); // For react-select

  // Get access token properly
  const accessToken = location.state?.access_token || localStorage.getItem('access_token');
  const autoLoggedIn = location.state?.autoLoggedIn || false;
  // Debug: Log current user data
  useEffect(() => {
  }, [user]);

  // Update form when user data becomes available
  useEffect(() => {
    if (user && (user.user_id || user.id)) {

      // Check if profile is already complete and redirect
      if (user.profile_status === 'complete') {
        navigate('/social');
        return;
      }

      updateFormWithUserData(user);
    }
  }, [user, navigate]);

  // Function to update form with user data
  const updateFormWithUserData = (userData) => {

    // Update first name
    if (userData.first_name) {
      setFormData(prev => ({
        ...prev,
        firstName: userData.first_name
      }));
    }

    // Update last name
    if (userData.last_name) {
      setFormData(prev => ({
        ...prev,
        lastName: userData.last_name
      }));
    }

    // Update sub type (for pet guardians only)
    if (userData.sub_type) {
      setFormData(prev => ({
        ...prev,
        subType: userData.sub_type
      }));
    }

    // Update gender
    if (userData.gender) {
      setFormData(prev => ({
        ...prev,
        gender: userData.gender.toLowerCase()
      }));
    }

    // Update date of birth
    if (userData.date_of_birth) {
      try {
        const birthDate = new Date(userData.date_of_birth);
        if (!isNaN(birthDate.getTime())) {
          setFormData(prev => ({
            ...prev,
            dateOfBirth: birthDate
          }));
        }
      } catch (error) {
        console.error('Error parsing date of birth:', error);
      }
    }

    // Update country - try to find matching country option
    if (userData.country_id) {
      const countryOption = countryOptions.find(opt => opt.value === userData.country_id);
      if (countryOption) {
        setFormData(prev => ({
          ...prev,
          country: countryOption.value
        }));
      }
    } else if (userData.country) { // Fallback if country_id is not directly available, try matching by name
      const countryOption = countryOptions.find(opt => opt.label.toLowerCase() === userData.country.toLowerCase());
      if (countryOption) {
        setFormData(prev => ({
          ...prev,
          country: countryOption.value
        }));
      }
    }

    // Update city - will be set after cities are fetched (handled in useEffect)
    // Note: city_id will be set after cities are loaded for the selected country

    // Update bio
    if (userData.bio) {
      setFormData(prev => ({
        ...prev,
        bio: userData.bio
      }));
    }

    // Update sub_type
    if (userData.sub_type) {
      setFormData(prev => ({
        ...prev,
        subType: userData.sub_type
      }));
    }

    // Update firm specific fields
    if (userData.user_type === 'FIRM' || userType?.toLowerCase() === 'firm') {
      setFormData(prev => ({
        ...prev,
        nameEn: userData.name_en || userData.first_name || '',
        nameAr: userData.name_ar || '',
        legalNumber: userData.legal_number || '',
        address: userData.address || '',
        contactInfo: userData.contact_info || '',
        subType: userData.sub_type || ''
      }));
    }

    // Update profile image
    if (userData.profile_image_url) {
      setProfileImagePreview(userData.profile_image_url);
    } else if (userData.profile_image) {
      setProfileImagePreview(userData.profile_image);
    } else if (userData.logo_path) {
      setProfileImagePreview(userData.logo_path);
    }


  };

  // Function to get user profile from backend
  const getUserProfile = async (token) => {
    if (!token || !mounted.current) return null;

    // Skip server verification during profile creation - use localStorage data instead
    const storedUserData = localStorage.getItem('user_data');
    if (storedUserData) {
      try {
        const userData = JSON.parse(storedUserData);
        if (userData && (userData.id || userData.user_id)) {
          return userData;
        }
      } catch (error) {
        console.warn('⚠️ Failed to parse stored user data during profile creation');
      }
    }
    return null;

    // Original server verification code (commented out during profile creation)
    /*
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${API_BASE_URL}/api/v1/user-profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();

        // Handle different response structures
        let userData = null;
        if (data.status === 'success' && data.data) {
          userData = data.data; // Laravel success response with data wrapper
        } else if (data.user) {
          userData = data.user;
        } else if (data.id || data.user_id) {
          userData = data;
        }

        if (userData && (userData.id || userData.user_id)) {
          localStorage.setItem('user_data', JSON.stringify(userData));
          setUser(userData);
          
          // Update form with user data
          updateFormWithUserData(userData);
          
          return userData;
        }
      } else if (response.status === 401) {
        return null;
      }

      console.warn('⚠️ Unexpected response:', response.status);
      return null;

    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('⏱️ Token validation timed out');
      } else {
        console.error('❌ Token validation error:', error);
      }
      return null;
    }
    */
  };

  // Form state - Initialize with empty data, will be updated when user data is available
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nameEn: '',
    nameAr: '',
    legalNumber: '',
    address: '',
    contactInfo: '',
    gender: '',
    dateOfBirth: null,
    country: '',
    city: '',
    referralCode: '',
    bio: '',
    profileImage: null,
    subType: '',
    specialities: [], // Array of speciality IDs for service providers
    logo: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Refs for date of birth input (for flatpickr)
  const dateOfBirthInputRef = useRef(null);
  const dateOfBirthPickerRef = useRef(null);



  // User type configurations - Updated titles/subtitles from guideline, kept original colors/gradients
  const userTypeConfig = {
    pet_guardian: {
      title: t('completeProfile.roles.petGuardian.title'),
      subtitle: t('completeProfile.roles.petGuardian.subtitle'),
      primaryColor: '#8548A8', // Kept original primaryColor from the provided CompleteProfile.jsx file
      buttonColor: '#8548A8',
      mainCircleGradient: 'linear-gradient(135deg, #8548A8 0%, #2A8AD2 100%)',
      floatingCircle1: 'linear-gradient(135deg, #FFA055 0%, #8548A8 100%)',
      floatingCircle2: '#8548A8',
      floatingCircle3: 'linear-gradient(135deg, #FFA055 0%, #FF6B9D 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(255, 160, 85, 0.6)'
    },
    veterinarian: {
      title: t('completeProfile.roles.veterinarian.title'), // Updated title from guideline
      subtitle: t('completeProfile.roles.veterinarian.subtitle'), // Updated subtitle from guideline
      primaryColor: '#00D4B3', // Kept original primaryColor from the provided CompleteProfile.jsx file
      buttonColor: '#00D4B3',
      mainCircleGradient: 'linear-gradient(135deg, #2A8AD2 0%, #00D483 100%)',
      floatingCircle1: 'linear-gradient(135deg, #00D4B3 0%, #2A8AD2 100%)',
      floatingCircle2: '#00D4B3',
      floatingCircle3: 'linear-gradient(135deg, #2A8AD2 0%, #00D4B3 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(0, 212, 179, 0.6)'
    },
    clinic_moderator: {
      title: t('completeProfile.roles.clinicModerator.title'), // Updated title from guideline
      subtitle: t('completeProfile.roles.clinicModerator.subtitle'), // Updated subtitle from guideline
      primaryColor: '#00D4B3', // Kept original primaryColor from the provided CompleteProfile.jsx file
      buttonColor: '#00D4B3',
      mainCircleGradient: 'linear-gradient(135deg, #2A8AD2 0%, #00D483 100%)',
      floatingCircle1: 'linear-gradient(135deg, #00D4B3 0%, #2A8AD2 100%)',
      floatingCircle2: '#00D4B3',
      floatingCircle3: 'linear-gradient(135deg, #2A8AD2 0%, #00D4B3 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(0, 212, 179, 0.6)'
    },
    service_provider: {
      title: t('completeProfile.roles.serviceProvider.title'), // Updated title from guideline
      subtitle: t('completeProfile.roles.serviceProvider.subtitle'), // Updated subtitle from guideline
      primaryColor: '#D65745', // Kept original primaryColor from the provided CompleteProfile.jsx file
      buttonColor: '#D65745',
      mainCircleGradient: 'linear-gradient(135deg, #D65745 0%, #00D483 100%)',
      floatingCircle1: 'linear-gradient(135deg, #D65745 0%, #00D4B3 100%)',
      floatingCircle2: '#D65745',
      floatingCircle3: 'linear-gradient(135deg, #00D4B3 0%, #D65745 100%)',
      floatingCircle4: '#00D4B3',
      floatingCircle5: 'rgba(214, 87, 69, 0.6)'
    },
    firm: {
      title: t('completeProfile.roles.firm.title'),
      subtitle: t('completeProfile.roles.firm.subtitle'),
      primaryColor: '#000080',
      buttonColor: '#000080',
      mainCircleGradient: 'linear-gradient(135deg, #000080 0%, #2A8AD2 100%)',
      floatingCircle1: 'linear-gradient(135deg, #000080 0%, #FFA055 100%)',
      floatingCircle2: '#000080',
      floatingCircle3: 'linear-gradient(135deg, #2A8AD2 0%, #000080 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(0, 0, 128, 0.6)'
    }
  };

  const config = userType && userTypeConfig[userType.toLowerCase()] ? userTypeConfig[userType.toLowerCase()] : userTypeConfig.pet_guardian;

  // Apply user type color to Flatpickr calendars
  useFlatpickrUserType();

  // Enhanced authentication and user data validation
  useEffect(() => {
    const validateUserAndToken = async () => {
      const token = localStorage.getItem('access_token');

      if (!token) {
        console.error('No access token found. Redirecting to registration.');
        navigate(`/auth/register/${userType}`);
        return;
      }

      // Check if we have user data from location state (Google OAuth flow)
      if (location.state?.user) {

        // Check if profile is already complete and redirect immediately
        if (location.state.user.profile_status === 'complete') {
          navigate('/social');
          return;
        }

        setUser(location.state.user);
        setIsValidating(false);
        return;
      }

      // Check if we have user data from localStorage
      const storedUserData = localStorage.getItem('user_data');
      if (storedUserData) {
        try {
          const parsedUserData = JSON.parse(storedUserData);
          if (parsedUserData.user_id || parsedUserData.id) {
            // Check if profile is already complete and redirect immediately
            if (parsedUserData.profile_status === 'complete') {
              navigate('/social');
              return;
            }

            setUser(parsedUserData);
            setIsValidating(false);
            return;
          }
        } catch (error) {
          console.error('Error parsing stored user data:', error);
        }
      }

      // If no user data available, try to fetch from backend
      const validatedUser = await getUserProfile(token);
      if (mounted.current) {
        if (validatedUser) {

          // Check if profile is already complete and redirect immediately
          if (validatedUser.profile_status === 'complete') {
            navigate('/social');
            return;
          }

          setUser(validatedUser);
        } else {
          console.error('❌ Token validation failed. Redirecting to registration.');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_data');
          navigate(`/auth/register/${userType}`);
          return;
        }
        setIsValidating(false);
      }
    };

    validateUserAndToken();

    return () => {
      mounted.current = false;
    };
  }, [navigate, userType, location.state]);

  // Fetch countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/locations/countries-only`);
        if (!response.ok) {
          throw new Error('Failed to fetch countries');
        }
        const result = await response.json();
        if (result.status === 'success' && Array.isArray(result.data)) {
          setCountries(result.data);
        }
      } catch (err) {
        console.error('Error fetching countries:', err);
        setError(t('completeProfile.errors.countries'));
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (Array.isArray(countries) && countries.length > 0) {
      const options = countries.map(country => ({
        value: country.id,
        label: isRTL ? (country.name_ar || country.name_en) : country.name_en
      }));
      setCountryOptions(options);
    }
  }, [countries, isRTL]);

  // Fetch specialities from API (only for service providers)
  useEffect(() => {
    if (userType?.toLowerCase() !== 'service_provider') {
      return;
    }

    const fetchSpecialities = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/service-provider-specialities`);
        if (!response.ok) {
          throw new Error('Failed to fetch specialities');
        }
        const result = await response.json();
        if (result.status === 'success' && Array.isArray(result.data)) {
          setSpecialities(result.data);
          const options = result.data.map(speciality => ({
            value: speciality.id,
            label: isRTL ? (speciality.name_ar || speciality.name_en) : speciality.name_en
          }));
          setSpecialityOptions(options);
        }
      } catch (err) {
        console.error('Error fetching specialities:', err);
      }
    };
    fetchSpecialities();
  }, [userType, isRTL]);

  // Fetch cities when country is selected
  useEffect(() => {
    const fetchCities = async () => {
      if (!formData.country) {
        setCities([]);
        setCityOptions([]);
        // Clear city selection when country is cleared
        setFormData(prev => ({ ...prev, city: '' }));
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/locations/cities/country/${formData.country}`);
        if (!response.ok) {
          throw new Error('Failed to fetch cities');
        }
        const result = await response.json();
        if (result.status === 'success' && Array.isArray(result.data)) {
          setCities(result.data);
          const options = result.data.map(city => ({
            value: city.id,
            label: isRTL ? (city.name_ar || city.name_en) : city.name_en
          }));
          setCityOptions(options);
        }
      } catch (err) {
        console.error('Error fetching cities:', err);
        setCities([]);
        setCityOptions([]);
      }
    };

    fetchCities();
  }, [formData.country, isRTL]);

  // Set city from user data after cities are loaded
  useEffect(() => {
    if (user && user.city_id && cityOptions.length > 0) {
      const cityOption = cityOptions.find(opt => opt.value === user.city_id);
      if (cityOption && formData.city !== cityOption.value) {
        setFormData(prev => ({
          ...prev,
          city: cityOption.value
        }));
      }
    }
  }, [user, cityOptions, formData.city]);

  // Sub type options for PET_GUARDIAN users
  const subTypeOptions = [
    { value: 'parent', label: t('completeProfile.fields.subType.options.parent') },
    { value: 'influencer', label: t('completeProfile.fields.subType.options.influencer') },
    { value: 'rescuer', label: t('completeProfile.fields.subType.options.rescuer') }
  ];

  // Sub type options for VETERINARIAN users
  const vetSubTypeOptions = [
    { value: 'clinic_owner', label: t('completeProfile.fields.subType.options.clinic_owner') },
    { value: 'clinic_member', label: t('completeProfile.fields.subType.options.clinic_member') },
    { value: 'freelancer', label: t('completeProfile.fields.subType.options.freelancer') }
  ];

  // Check if user is PET_GUARDIAN
  const isPetGuardian = userType?.toLowerCase() === 'pet_guardian' || user?.user_type === 'PET_GUARDIAN';

  // Check if user is VETERINARIAN
  const isVeterinarian = userType?.toLowerCase() === 'veterinarian' || user?.user_type === 'VETERINARIAN';

  // Check if user is FIRM
  const isFirm = userType?.toLowerCase() === 'firm' || user?.user_type === 'FIRM' || (user?.user_type?.value && user.user_type.value === 'FIRM');

  // Sub type options for FIRM users
  const firmSubTypeOptions = [
    { value: 'government', label: t('completeProfile.fields.subType.options.government') || 'Government' },
    { value: 'associate', label: t('completeProfile.fields.subType.options.associate') || 'Associate' },
    { value: 'sponsor', label: t('completeProfile.fields.subType.options.sponsor') || 'Sponsor (Private Sector)' }
  ];


  // Initialize flatpickr for date of birth
  useEffect(() => {
    // Helper function to initialize flatpickr
    const initializeFlatpickr = (element) => {
      // Clean up existing instance if any
      if (dateOfBirthPickerRef.current) {
        dateOfBirthPickerRef.current.destroy();
        dateOfBirthPickerRef.current = null;
      }

      const flatpickrInstance = flatpickr(element, {
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        allowInput: true,
        clickOpens: true,
        onChange: function (selectedDates, dateStr) {
          if (selectedDates.length > 0) {
            setFormData(prev => ({ ...prev, dateOfBirth: selectedDates[0] }));
            if (validationErrors.dateOfBirth) {
              setValidationErrors(prev => ({ ...prev, dateOfBirth: '' }));
            }
          }
        }
      });
      dateOfBirthPickerRef.current = flatpickrInstance;
    };

    // Use both ref and ID selector for reliability
    const inputElement = dateOfBirthInputRef.current || document.getElementById('date-of-birth-input');

    if (!inputElement) {
      // If element not found, retry after a short delay
      const timer = setTimeout(() => {
        const retryElement = dateOfBirthInputRef.current || document.getElementById('date-of-birth-input');
        if (retryElement && !dateOfBirthPickerRef.current) {
          initializeFlatpickr(retryElement);
        }
      }, 100);
      return () => clearTimeout(timer);
    }

    // Initialize flatpickr
    initializeFlatpickr(inputElement);

    return () => {
      if (dateOfBirthPickerRef.current) {
        dateOfBirthPickerRef.current.destroy();
        dateOfBirthPickerRef.current = null;
      }
    };
  }, [isValidating]); // Re-run when validation completes and form is rendered

  // Update flatpickr value when formData.dateOfBirth changes
  useEffect(() => {
    if (dateOfBirthPickerRef.current && formData.dateOfBirth) {
      dateOfBirthPickerRef.current.setDate(formData.dateOfBirth, false);
    } else if (dateOfBirthPickerRef.current && !formData.dateOfBirth) {
      dateOfBirthPickerRef.current.clear();
    }
  }, [formData.dateOfBirth]);

  // Show loading state while validating
  if (isValidating) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: '#FAFAFA',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Red Hat Display", sans-serif'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <Loader2 size={48} className="animate-spin" style={{ color: config.primaryColor }} />
          <p style={{
            fontFamily: '"Red Hat Display", sans-serif',
            fontSize: '18px',
            color: '#6B7280',
            margin: 0
          }}>
            {t('completeProfile.status.validating')}
          </p>
        </div>
      </div>
    );
  }

  // Validation function (kept existing logic as per guideline)
  const validateForm = () => {
    const errors = {};

    // Country validation - required field
    if (!formData.country) {
      errors.country = t('completeProfile.errors.countryRequired');
    }

    // City validation - now required so downstream features
    // (like adoption filters) can rely on it.
    if (!formData.city) {
      errors.city = t('completeProfile.errors.cityRequired');
    }

    // Sub type validation - required for pet guardians, veterinarians and firms
    if ((isPetGuardian || isVeterinarian || isFirm) && !formData.subType) {
      errors.subType = t('completeProfile.errors.subTypeRequired');
    }

    // Firm specific validation
    if (isFirm) {
      if (!formData.nameEn) errors.nameEn = t('completeProfile.errors.nameEnRequired') || 'Organization name in English is required';
      if (!formData.contactInfo) errors.contactInfo = t('completeProfile.errors.contactInfoRequired') || 'Contact information is required';
    }

    // Date of birth validation
    if (formData.dateOfBirth) {
      const today = new Date();
      const birthDate = new Date(formData.dateOfBirth);
      const age = today.getFullYear() - birthDate.getFullYear();

      if (age < 13) {
        errors.dateOfBirth = t('completeProfile.errors.dobUnderage');
      }
      if (birthDate > today) {
        errors.dateOfBirth = t('completeProfile.errors.dobFuture');
      }
    }

    // Bio length validation
    if (formData.bio && formData.bio.length > 500) {
      errors.bio = t('completeProfile.errors.bioTooLong');
    }

    return errors;
  };

  // Handle input change (kept existing logic as per guideline)
  const handleInputChange = (e) => {
    let { name, value } = e.target;

    // Only allow numbers for contactInfo
    if (name === 'contactInfo') {
      value = value.replace(/[^0-9]/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');

    // Clear specific validation error
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle profile image upload (kept existing logic as per guideline)
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('completeProfile.errors.imageType'));
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('completeProfile.errors.imageSize'));
      return;
    }

    setImageUploading(true);
    setError('');

    try {
      // Create preview immediately
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Upload image to server
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('completeProfile.errors.authMissing'));
        return;
      }

      const formData = new FormData();
      formData.append('profile_image', file, file.name);
      const response = await fetch(`${API_BASE_URL}/api/v1/user-profile/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Image upload failed: ${response.status}`);
      }

      const imageResult = await response.json();
      const uploadedImageUrl = imageResult.imageUrl || imageResult.url;


      // Store the uploaded image URL for later use
      setFormData(prev => ({
        ...prev,
        profileImage: uploadedImageUrl
      }));

    } catch (error) {
      console.error('Error uploading image:', error);
      setError(t('completeProfile.errors.imageUpload'));
      // Remove the preview if upload failed
      setProfileImagePreview(null);
    } finally {
      setImageUploading(false);
    }
  };

  // Handle logo upload for firms
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageUploading(true);
    setError('');

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImagePreview(e.target.result); // Reuse preview state
      };
      reader.readAsDataURL(file);

      const token = localStorage.getItem('access_token');
      const formDataUpload = new FormData();
      formDataUpload.append('logo', file, file.name);

      const response = await fetch(`${API_BASE_URL}/api/v1/user-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error(`Logo upload failed: ${response.status}`);
      }

      const result = await response.json();
      const logoPath = result.data?.logo_path || result.logo_path;

      setFormData(prev => ({
        ...prev,
        logo: logoPath
      }));

    } catch (error) {
      console.error('Error uploading logo:', error);
      setError(t('completeProfile.errors.imageUpload'));
      setProfileImagePreview(null);
    } finally {
      setImageUploading(false);
    }
  };

  // âœ… CORRECTED: Profile completion with proper gender mapping and dashboard redirect
  const handleCompleteProfile = async () => {
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError(t('completeProfile.errors.formGeneric'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('access_token');

      if (!token) {
        setError(t('completeProfile.errors.authMissing'));
        navigate(`/auth/register/${userType}`);
        return;
      }

      // âœ… CORRECTED: Map gender values to backend format
      const genderMapping = {
        'female': 'female',
        'male': 'male'
      };

      // Prepare form data for submission
      const profileData = new FormData();

      if (formData.firstName) profileData.append('first_name', formData.firstName);
      if (formData.lastName) profileData.append('last_name', formData.lastName);
      if (formData.subType) profileData.append('sub_type', formData.subType);
      if (formData.gender) {
        // âœ… Map frontend gender to backend format
        const backendGender = genderMapping[formData.gender];
        if (backendGender) {
          profileData.append('gender', backendGender);
        }
      }

      if (formData.dateOfBirth) {
        // Format date in local timezone to avoid timezone conversion issues
        const date = new Date(formData.dateOfBirth);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        profileData.append('date_of_birth', formattedDate);
      }
      if (formData.country) profileData.append('country_id', formData.country);
      if (formData.city) profileData.append('city_id', formData.city);
      if (formData.referralCode?.trim()) profileData.append('referral_code', formData.referralCode.trim());
      if (formData.bio) profileData.append('bio', formData.bio);
      // Append speciality_ids for service providers
      if (userType?.toLowerCase() === 'service_provider' && formData.specialities && formData.specialities.length > 0) {
        formData.specialities.forEach((specialityId) => {
          profileData.append('speciality_ids[]', specialityId);
        });
      }

      // Firm specific fields
      if (isFirm) {
        if (formData.nameAr) profileData.append('name_ar', formData.nameAr);
        if (formData.nameEn) profileData.append('name_en', formData.nameEn);
        if (formData.legalNumber) profileData.append('legal_number', formData.legalNumber);
        if (formData.address) profileData.append('address', formData.address);
        if (formData.contactInfo) profileData.append('contact_info', formData.contactInfo);
        // logo is already handled via uploadLogo if it was uploaded separately, 
        // but if it's stored in formData.logo as a path, we don't need to append the file here 
        // unless we want to upload it during this final step.
        // The current implementation of handleLogoUpload already updates the profile on the backend.
      }
      // Don't append profile image here since it's already uploaded
      profileData.append('profile_status', 'complete');
      // Update user profile
      const response = await fetch(`${API_BASE_URL}/api/v1/user-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}` // FormData does not need 'Content-Type' header
        },
        body: profileData
      });

      if (response.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setError(t('completeProfile.errors.sessionExpired'));
        navigate(`/auth/register/${userType}`);
        return;
      }

      const data = await response.json();

      if (response.ok) {

        // Update stored user data with the response
        const updatedUser = { ...data.data };

        localStorage.setItem('user_data', JSON.stringify(updatedUser));
        setUser(updatedUser); // Update component state

        // Authenticate the user in the auth context
        try {
          const token = localStorage.getItem('access_token');
          await oauthLogin(updatedUser, token);
        } catch (error) {
          console.error('Error authenticating user after profile completion:', error)
        }

        // Navigate to dashboard
        navigate('/social', {
          state: {
            registrationComplete: true,
            user: updatedUser
          }
        });

      } else {
        if (response.status === 422) {
          // Handle validation errors
          let hasFieldValidationErrors = false;

          if (data.errors) {
            const fieldErrors = {};
            if (data.errors.referral_code?.length) {
              fieldErrors.referralCode = data.errors.referral_code[0];
            }
            if (Object.keys(fieldErrors).length > 0) {
              hasFieldValidationErrors = true;
              setValidationErrors(prev => ({ ...prev, ...fieldErrors }));
            }
          }

          const detail = data.detail;
          if (Array.isArray(detail)) {
            const fieldErrors = {};
            detail.forEach(error => {
              if (error.loc && error.loc.length > 1) {
                const fieldKey = error.loc[1];
                switch (fieldKey) {
                  case 'country_id':
                    fieldErrors.country = t('completeProfile.errors.countryRequired');
                    break;
                  case 'date_of_birth':
                    fieldErrors.dateOfBirth = t('completeProfile.errors.dobFuture');
                    break;
                  case 'bio':
                    fieldErrors.bio = t('completeProfile.errors.bioTooLong');
                    break;
                  case 'referral_code':
                    fieldErrors.referralCode = error.msg;
                    break;
                  case 'first_name':
                    fieldErrors.firstName = error.msg;
                    break;
                  case 'last_name':
                    fieldErrors.lastName = error.msg;
                    break;
                  default:
                    fieldErrors[fieldKey] = error.msg;
                }
              }
            });
            if (Object.keys(fieldErrors).length > 0) {
              hasFieldValidationErrors = true;
            }
            setValidationErrors(fieldErrors);
          }
          setError(hasFieldValidationErrors ? '' : t('completeProfile.errors.submitGeneric'));
        } else {
          setError(data.detail || t('completeProfile.errors.submitGeneric'));
        }
      }
    } catch (error) {
      console.error('Profile completion error:', error);
      setError(t('completeProfile.errors.network'));
    } finally {
      setIsLoading(false);
    }
  };

  // âœ… NEW: Skip profile completion and go directly to dashboard
  const handleSkipProfile = async () => {
    setIsLoading(true); // Indicate loading for the skip action

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('access_token');

      if (token) {
        // Mark profile step as completed even if skipped
        try {
          await fetch(`${API_BASE_URL}/api/v1/auth/registration/progress`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              step: 'profile_completion',
              completed: true
            })
          });
        } catch (error) {
          console.warn('Failed to update registration progress:', error);
        }
      }

      // Navigate to dashboard
      navigate('/social', {
        state: {
          registrationComplete: true,
          profileSkipped: true,
          user: user // Pass current user data
        }
      });
    } catch (error) {
      console.error('Skip profile error:', error);
      // Still navigate even if progress update fails
      navigate('/social', {
        state: {
          registrationComplete: true,
          profileSkipped: true,
          user: user
        }
      });
    } finally {
      setIsLoading(false); // End loading
    }
  };


  // Custom styles for react-select (kept existing)
  const selectStyles = {
    control: (baseStyles, state) => ({
      ...baseStyles,
      height: '56px',
      minHeight: '56px',
      borderRadius: '14px',
      padding: '0 20px',
      border: `1.5px solid ${validationErrors.country || validationErrors.city
        ? '#EF4444'
        : state.isFocused
          ? config.primaryColor
          : '#E5E7EB'
        }`,
      boxShadow: 'none',
      fontFamily: '"Red Hat Display", sans-serif',
      textAlign: isRTL ? 'right' : 'left',
      '&:hover': {
        borderColor: validationErrors.country || validationErrors.city
          ? '#EF4444'
          : config.primaryColor,
      },
    }),
    valueContainer: (baseStyles) => ({
      ...baseStyles,
      height: '54px',
      padding: '0 20px',
      justifyContent: isRTL ? 'flex-end' : 'flex-start'
    }),
    placeholder: (baseStyles) => ({
      ...baseStyles,
      fontFamily: '"Red Hat Display", sans-serif',
      fontSize: '16px',
      color: '#9CA3AF',
      textAlign: isRTL ? 'right' : 'left'
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      fontFamily: '"Red Hat Display", sans-serif',
      fontSize: '16px',
      color: '#1F2937',
      textAlign: isRTL ? 'right' : 'left'
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      borderRadius: '14px',
      border: '1px solid #E5E7EB',
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
      zIndex: 9999
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (baseStyles, state) => ({
      ...baseStyles,
      fontFamily: '"Red Hat Display", sans-serif',
      fontSize: '16px',
      backgroundColor: state.isSelected
        ? config.primaryColor
        : state.isFocused
          ? `${config.primaryColor}10`
          : 'white',
      color: state.isSelected ? 'white' : '#1F2937',
      '&:hover': {
        backgroundColor: state.isSelected ? config.primaryColor : `${config.primaryColor}20`,
      },
    }),
    dropdownIndicator: (baseStyles) => ({
      ...baseStyles,
      padding: '8px 15px', // Adjust padding as needed to position the arrow
    }),
    indicatorSeparator: (baseStyles) => ({
      ...baseStyles,
      display: 'none', // Remove the separator line next to the arrow
    }),
  };


  return (
    <>
      {/* Global styles for DatePicker and animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .animate-spin {
            animation: spin 1s linear infinite;
          }

          /* Flatpickr custom styles */
          .flatpickr-calendar {
            font-family: '"Red Hat Display"', sans-serif !important;
            border-radius: 14px !important;
            border: 1px solid #E5E7EB !important;
            box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important;
          }

          .flatpickr-months {
            background-color: ${config.primaryColor} !important;
            border-radius: 14px 14px 0 0 !important;
          }

          .flatpickr-current-month {
            color: white !important;
            font-family: '"Red Hat Display"', sans-serif !important;
          }

          .flatpickr-weekdays {
            background-color: ${config.primaryColor} !important;
          }

          .flatpickr-weekday {
            color: white !important;
            font-family: '"Red Hat Display"', sans-serif !important;
          }

          .flatpickr-day:hover {
            background-color: ${config.primaryColor}20 !important;
          }

          .flatpickr-day.selected {
            background-color: ${config.primaryColor} !important;
            border-color: ${config.primaryColor} !important;
          }

          .flatpickr-day.today {
            border-color: ${config.primaryColor} !important;
          }

          .flatpickr-day.today.selected {
            background-color: ${config.primaryColor} !important;
          }

          /* Month dropdown styling */
          .flatpickr-monthDropdown-month {
            background-color: #FFFFFF !important;
            color: #1F2937 !important;
            border: 1px solid #E5E7EB !important;
            border-radius: 8px !important;
            padding: 8px 12px !important;
            font-family: '"Red Hat Display"', sans-serif !important;
            font-size: 14px !important;
            font-weight: 500 !important;
            cursor: pointer !important;
            transition: all 0.2s ease !important;
          }

          .flatpickr-monthDropdown-month:hover {
            background-color: #F9FAFB !important;
            border-color: ${config.primaryColor} !important;
            color: ${config.primaryColor} !important;
          }

          .flatpickr-monthDropdown-month:focus {
            outline: none !important;
            border-color: ${config.primaryColor} !important;
            box-shadow: 0 0 0 3px ${config.primaryColor}20 !important;
          }

          /* Year dropdown styling */
          .flatpickr-monthDropdown-year {
            background-color: #FFFFFF !important;
            color: #1F2937 !important;
            border: 1px solid #E5E7EB !important;
            border-radius: 8px !important;
            padding: 8px 12px !important;
            font-family: '"Red Hat Display"', sans-serif !important;
            font-size: 14px !important;
            font-weight: 500 !important;
            cursor: pointer !important;
            transition: all 0.2s ease !important;
          }

          .flatpickr-monthDropdown-year:hover {
            background-color: #F9FAFB !important;
            border-color: ${config.primaryColor} !important;
            color: ${config.primaryColor} !important;
          }

          .flatpickr-monthDropdown-year:focus {
            outline: none !important;
            border-color: ${config.primaryColor} !important;
            box-shadow: 0 0 0 3px ${config.primaryColor}20 !important;
          }

          /* Month select dropdown options */
          .flatpickr-monthSelect-month {
            background-color: #FFFFFF !important;
            color: #1F2937 !important;
          }

          .flatpickr-monthSelect-month:hover {
            background-color: ${config.primaryColor}10 !important;
            color: ${config.primaryColor} !important;
          }

          /* Select elements in flatpickr header */
          .flatpickr-current-month select {
            background-color: #FFFFFF !important;
            color: #1F2937 !important;
            border: 1px solid rgba(255, 255, 255, 0.3) !important;
            border-radius: 6px !important;
            padding: 4px 8px !important;
            font-family: '"Red Hat Display"', sans-serif !important;
            font-size: 14px !important;
            font-weight: 500 !important;
            cursor: pointer !important;
            margin: 0 4px !important;
            transition: all 0.2s ease !important;
          }

          .flatpickr-current-month select:hover {
            background-color: rgba(255, 255, 255, 0.9) !important;
            border-color: rgba(255, 255, 255, 0.5) !important;
          }

          .flatpickr-current-month select:focus {
            outline: none !important;
            border-color: rgba(255, 255, 255, 0.7) !important;
            box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3) !important;
          }

          /* Select dropdown options */
          .flatpickr-current-month select option {
            background-color: #FFFFFF !important;
            color: #1F2937 !important;
            padding: 8px !important;
          }

          /* Responsive styles */
          @media (max-width: 768px) {
            .profile-container {
              flex-direction: column;
            }

            .left-panel, .right-panel {
              width: 100% !important;
              height: auto !important;
            }

            .form-container {
              position: relative !important;
              top: auto !important;
              left: auto !important;
              transform: none !important;
              padding: 20px !important;
            }
          }
        `}
      </style>

      <div className="profile-responsive-root" dir={isRTL ? 'rtl' : 'ltr'} lang={i18n.language}
        style={{
          width: '100vw',
          height: '100vh',
          background: '#FAFAFA',
          position: 'fixed',
          top: 0,
          left: 0,
          fontFamily: '"Red Hat Display", sans-serif',
          overflow: 'hidden',
          display: 'flex',
          direction: isRTL ? 'rtl' : 'ltr'
        }}>
        {/* Header Component - Inline */}
        <div className="profile-header"
          style={{
            position: "absolute",
            top: "0",
            left: "0",
            width: "100%",
            height: "100px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 60px",
            zIndex: 10,
            boxSizing: "border-box",
          }}
        >
          {/* Logo */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
            }}
            onClick={() => navigate("/")}
          >
            <img src="/Landing page assets/Sulhafa_Logo.png" alt="Sulhafa" style={{ height: '70px', width: 'auto', display: 'block' }} />
            <img src="/Landing page assets/Sulhafa_Logo_Text.png" alt="Sulhafa" style={{ height: '42px', width: 'auto', display: 'block' }} />
          </div>

        </div>

        {/* Left Section - Design Panel */}
        <div className="profile-left-panel">
          {/* Title Section */}
          <div style={{
            position: 'absolute',
            top: '90px',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            zIndex: 3
          }}>
            <h1 style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '48px',
              lineHeight: '1.2',
              color: '#1a1a1a',
              margin: '0 0 12px 0',
              letterSpacing: '-0.02em'
            }}>
              {config.title}
            </h1>
            {!isFirm && (
              <p style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 400,
                fontSize: '18px',
                lineHeight: '1.6',
                color: '#6B7280',
                margin: 0
              }}>
                {config.subtitle}
              </p>
            )}
          </div>

          {/* Main Circle with Character */}
          <div style={{
            position: 'relative',
            width: '540px',
            height: '540px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2
          }}>

            <img
              src={userType === "firm" ? "/character/business.png" : `/images/${userType === "pet_guardian"
                ? "register-petowner.png"
                : userType === "veterinarian"
                  ? "register-vet.png"
                  : "register-service.png"
                }`}
              alt={`${config.title} character`}
              style={{
                position: "relative",
                width: "540px",
                height: "540px",
                objectFit: "cover",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                transform: userType !== "service_provider" ? "scaleX(-1)" : "none",
                zIndex: 2,
                transform: isRTL ? 'scaleX(-1)' : 'none'
              }}
              onError={(e) => {
                console.error(`Failed to load image: ${e.target.src}`)
                e.target.style.display = "none"
              }}
            />
          </div>
        </div>
        {/* Right Section - Profile Completion Form */}
        <div className="profile-right-panel">
          {/* Progress Steps */}
          <div className="profile-progress-steps" style={{
            position: 'absolute',
            top: '140px',
            left: '0',
            right: '0',
            width: '100%',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 80px',
            boxSizing: 'border-box'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              maxWidth: '600px',
              flexDirection: isRTL ? 'row-reverse' : 'row'
            }}>
              {/* Step 1 - Completed */}
              <div style={{
                width: '40px',
                height: '40px',
                background: config.primaryColor,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: '18px',
                color: '#FFFFFF',
                zIndex: 2
              }}>
                1
              </div>

              {/* Connecting Line 1-2 */}
              <div style={{
                flex: 1,
                height: '3px',
                background: config.primaryColor,
                margin: '0 20px'
              }} />

              {/* Step 2 - Completed */}
              <div style={{
                width: '40px',
                height: '40px',
                background: config.primaryColor,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: '18px',
                color: '#FFFFFF',
                zIndex: 2
              }}>
                2
              </div>

              {/* Connecting Line 2-3 */}
              <div style={{
                flex: 1,
                height: '3px',
                background: config.primaryColor,
                margin: '0 20px'
              }} />

              {/* Step 3 - Current/Active */}
              <div style={{
                width: '40px',
                height: '40px',
                background: config.primaryColor,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: '18px',
                color: '#FFFFFF',
                zIndex: 2
              }}>
                3
              </div>
            </div>
          </div>

          {/* Form Content Container */}
          <div className="profile-form-content">
            {/* Form Header */}
            <div style={{
              textAlign: isRTL ? 'right' : 'center',
              marginBottom: '56px'
            }}>
              <h2 style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: '38px',
                lineHeight: '1.3',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                {t('completeProfile.headings.title')}
              </h2>
              <p style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 400,
                fontSize: '18px',
                lineHeight: '1.5',
                color: '#6B7280',
                margin: 0
              }}>
                {t('completeProfile.headings.subtitle')}
              </p>
            </div>

            {error && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '14px',
                  color: '#DC2626',
                  margin: 0
                }}>
                  {error}
                </p>
              </div>
            )}

            {/* Profile Image Upload */}
            <div className="profile-image-upload-container" style={{
              textAlign: 'center',
              marginBottom: '40px'
            }}>
              <div style={{
                position: 'relative',
                display: 'inline-block'
              }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  border: profileImagePreview ? 'none' : '3px dashed #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: profileImagePreview ? 'none' : 'transparent',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                  onClick={() => document.getElementById(isFirm ? 'logoInput' : 'profileImageInput').click()}
                >
                  {imageUploading ? (
                    <Loader2 size={32} color="#9CA3AF" className="animate-spin" />
                  ) : profileImagePreview ? (
                    <img
                      src={profileImagePreview}
                      alt="Profile"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: '#007AFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <UploadCloud size={20} color="#FFFFFF" />
                    </div>
                  )}

                  {profileImagePreview && (
                    <div style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: isRTL ? 'auto' : '8px',
                      left: isRTL ? '8px' : 'auto',
                      width: '24px',
                      height: '24px',
                      background: '#6B7280',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #FFFFFF'
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                        <path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4z" />
                        <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                      </svg>
                    </div>
                  )}
                </div>
                <input
                  id={isFirm ? 'logoInput' : 'profileImageInput'}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '14px',
                  color: '#6B7280',
                  margin: '8px 0 0 0'
                }}>
                  {imageUploading ? t('completeProfile.upload.uploading') : t('completeProfile.upload.label')}
                </p>
              </div>
            </div>

            {/* Firm Specific Fields */}
            {isFirm && (
              <>
                <div style={{ display: 'flex', gap: '20px', marginBottom: '32px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#374151',
                      display: 'block',
                      marginBottom: '10px',
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      {t('completeProfile.fields.nameEn.label')}
                    </label>
                    <input
                      type="text"
                      name="nameEn"
                      value={formData.nameEn}
                      onChange={handleInputChange}
                      placeholder={t('completeProfile.fields.nameEn.placeholder')}
                      style={{
                        width: '100%',
                        height: '56px',
                        padding: '0 20px',
                        border: `1.5px solid ${validationErrors.nameEn ? '#EF4444' : '#E5E7EB'}`,
                        borderRadius: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s ease',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                    {validationErrors.nameEn && <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>{validationErrors.nameEn}</p>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#374151',
                      display: 'block',
                      marginBottom: '10px',
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      {t('completeProfile.fields.nameAr.label')}
                    </label>
                    <input
                      type="text"
                      name="nameAr"
                      value={formData.nameAr}
                      onChange={handleInputChange}
                      placeholder={t('completeProfile.fields.nameAr.placeholder')}
                      style={{
                        width: '100%',
                        height: '56px',
                        padding: '0 20px',
                        border: `1.5px solid ${validationErrors.nameAr ? '#EF4444' : '#E5E7EB'}`,
                        borderRadius: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s ease',
                        textAlign: isRTL ? 'right' : 'left'
                      }}
                    />
                    {validationErrors.nameAr && <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>{validationErrors.nameAr}</p>}
                  </div>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#374151',
                    display: 'block',
                    marginBottom: '10px',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('completeProfile.fields.legalNumber.label')}
                  </label>
                  <input
                    type="text"
                    name="legalNumber"
                    value={formData.legalNumber}
                    onChange={handleInputChange}
                    placeholder={t('completeProfile.fields.legalNumber.placeholder')}
                    style={{
                      width: '100%',
                      height: '56px',
                      padding: '0 20px',
                      border: '1.5px solid #E5E7EB',
                      borderRadius: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#374151',
                    display: 'block',
                    marginBottom: '10px',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('completeProfile.fields.address.label')}
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder={t('completeProfile.fields.address.placeholder')}
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '16px 20px',
                      border: `1.5px solid ${validationErrors.address ? '#EF4444' : '#E5E7EB'}`,
                      borderRadius: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      fontFamily: 'inherit',
                      resize: 'none',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                  {validationErrors.address && <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>{validationErrors.address}</p>}
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#374151',
                    display: 'block',
                    marginBottom: '10px',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('completeProfile.fields.contactInfo.label')} <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="contactInfo"
                    value={formData.contactInfo}
                    onChange={handleInputChange}
                    inputMode="numeric"
                    placeholder={t('completeProfile.fields.contactInfo.placeholder')}
                    style={{
                      width: '100%',
                      height: '56px',
                      padding: '0 20px',
                      border: `1.5px solid ${validationErrors.contactInfo ? '#EF4444' : '#E5E7EB'}`,
                      borderRadius: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                  {validationErrors.contactInfo && <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>{validationErrors.contactInfo}</p>}
                </div>

                {/* Sub Type Selection for Firm */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#374151',
                    display: 'block',
                    marginBottom: '10px',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('completeProfile.fields.subType.label')} <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <Select
                    options={firmSubTypeOptions}
                    value={firmSubTypeOptions.find(opt => opt.value === formData.subType)}
                    onChange={(selectedOption) => {
                      setFormData(prev => ({ ...prev, subType: selectedOption ? selectedOption.value : '' }));
                      if (validationErrors.subType) {
                        setValidationErrors(prev => ({ ...prev, subType: '' }));
                      }
                    }}
                    placeholder={t('completeProfile.fields.subType.placeholder')}
                    isSearchable={false}
                    isRtl={isRTL}
                    styles={selectStyles}
                  />
                  {validationErrors.subType && (
                    <p style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: '14px',
                      color: '#EF4444',
                      margin: '4px 0 0 0',
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      {validationErrors.subType}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Gender Selection & DOB - Hidden for Firms */}
            {!isFirm && (
              <>
                {/* Gender Selection */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 500,
                    fontSize: '16px',
                    color: '#374151',
                    display: 'block',
                    marginBottom: '10px',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {t('completeProfile.gender.label')}
                  </label>
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                    justifyContent: isRTL ? 'flex-end' : 'flex-start'
                  }}>
                    {[
                      { value: 'female', label: t('completeProfile.gender.options.female') },
                      { value: 'male', label: t('completeProfile.gender.options.male') }
                    ].map((option) => (
                      <label key={option.value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        fontFamily: '"Red Hat Display", sans-serif',
                        fontSize: '14px',
                        color: '#374151',
                        flexDirection: isRTL ? 'row-reverse' : 'row'
                      }}>
                        <input
                          type="radio"
                          name="gender"
                          value={option.value}
                          checked={formData.gender === option.value}
                          onChange={handleInputChange}
                          style={{
                            width: '16px',
                            height: '16px',
                            accentColor: config.primaryColor
                          }}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Sub Type Selection - Only for PET_GUARDIAN users */}
            {isPetGuardian && (
              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '10px',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {(isPetGuardian || isVeterinarian) ? (t('completeProfile.fields.userType.label') || "User Type") : t('completeProfile.fields.subType.label')} <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <Select
                  options={subTypeOptions}
                  value={subTypeOptions.find(opt => opt.value === formData.subType)}
                  onChange={(selectedOption) => {
                    setFormData(prev => ({ ...prev, subType: selectedOption ? selectedOption.value : '' }));
                    if (validationErrors.subType) {
                      setValidationErrors(prev => ({ ...prev, subType: '' }));
                    }
                  }}
                  placeholder={(isPetGuardian || isVeterinarian) ? (t('completeProfile.fields.userType.placeholder') || "Select type") : t('completeProfile.fields.subType.placeholder')}
                  isSearchable={false}
                  isRtl={isRTL}
                  styles={selectStyles}
                />
                {validationErrors.subType && (
                  <p style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '14px',
                    color: '#EF4444',
                    margin: '4px 0 0 0',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {validationErrors.subType}
                  </p>
                )}
              </div>
            )}

            {/* Sub Type Selection - Only for VETERINARIAN users */}
            {isVeterinarian && (
              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '10px',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {(isPetGuardian || isVeterinarian) ? (t('completeProfile.fields.userType.label') || "User Type") : t('completeProfile.fields.subType.label')} <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <Select
                  options={vetSubTypeOptions}
                  value={vetSubTypeOptions.find(opt => opt.value === formData.subType)}
                  onChange={(selectedOption) => {
                    setFormData(prev => ({ ...prev, subType: selectedOption ? selectedOption.value : '' }));
                    if (validationErrors.subType) {
                      setValidationErrors(prev => ({ ...prev, subType: '' }));
                    }
                  }}
                  placeholder={(isPetGuardian || isVeterinarian) ? (t('completeProfile.fields.userType.placeholder') || "Select type") : t('completeProfile.fields.subType.placeholder')}
                  isSearchable={false}
                  isRtl={isRTL}
                  styles={selectStyles}
                />
                {validationErrors.subType && (
                  <p style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '14px',
                    color: '#EF4444',
                    margin: '4px 0 0 0',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {validationErrors.subType}
                  </p>
                )}
              </div>
            )}

            {/* Individual Specific Fields - Hidden for Firms */}
            {!isFirm && (
              <>
                <div className="profile-name-fields" style={{
                display: 'flex',
                gap: '24px',
                marginBottom: '32px',
                flexDirection: isRTL ? 'row-reverse' : 'row'
              }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '10px',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('completeProfile.fields.firstName.label')}
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder={t('completeProfile.fields.firstName.placeholder')}
                  style={{
                    width: '100%',
                    height: '56px',
                    padding: '0 20px',
                    border: '1.5px solid #E5E7EB',
                    borderRadius: '14px',
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '16px',
                    color: '#1F2937',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    background: '#FFFFFF',
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = config.primaryColor;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                  }}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '10px',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('completeProfile.fields.lastName.label')}
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder={t('completeProfile.fields.lastName.placeholder')}
                  style={{
                    width: '100%',
                    height: '56px',
                    padding: '0 20px',
                    border: '1.5px solid #E5E7EB',
                    borderRadius: '14px',
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '16px',
                    color: '#1F2937',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    background: '#FFFFFF',
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = config.primaryColor;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                  }}
                />
              </div>
            </div>


            {/* Date of Birth using flatpickr */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: '#374151',
                display: 'block',
                marginBottom: '10px',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('completeProfile.fields.dateOfBirth.label')}
              </label>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  ref={dateOfBirthInputRef}
                  id="date-of-birth-input"
                  type="text"
                  placeholder={t('completeProfile.fields.dateOfBirth.placeholder')}
                  readOnly
                  style={{
                    width: '100%',
                    height: '56px',
                    padding: isRTL ? '0 20px 0 50px' : '0 50px 0 20px',
                    border: `1.5px solid ${validationErrors.dateOfBirth ? '#EF4444' : '#E5E7EB'}`,
                    borderRadius: '14px',
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '16px',
                    color: '#1F2937',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    background: '#FFFFFF',
                    cursor: 'pointer',
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}
                  onFocus={(e) => {
                    if (!validationErrors.dateOfBirth) {
                      e.target.style.borderColor = config.primaryColor;
                    }
                  }}
                  onBlur={(e) => {
                    if (!validationErrors.dateOfBirth) {
                      e.target.style.borderColor = '#E5E7EB';
                    }
                  }}
                />
                <div style={{
                  position: 'absolute',
                  right: isRTL ? 'auto' : '20px',
                  left: isRTL ? '20px' : 'auto',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px'
                }}>
                  <Calendar
                    size={18}
                    style={{
                      color: '#9CA3AF',
                      strokeWidth: 2
                    }}
                  />
                </div>
              </div>
              {validationErrors.dateOfBirth && (
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '14px',
                  color: '#EF4444',
                  margin: '4px 0 0 0',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {validationErrors.dateOfBirth}
                </p>
              )}
            </div>
              </>
            )}

            {/* Country and City using react-select */}
            <div className="profile-country-fields" style={{
              display: 'flex',
              gap: '24px',
              marginBottom: '40px',
              flexDirection: isRTL ? 'row-reverse' : 'row'
            }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '10px',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('completeProfile.fields.country.label')} <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <Select
                    options={countryOptions}
                    value={countryOptions.find(opt => opt.value === formData.country)}
                    onChange={(selectedOption) => {
                      setFormData(prev => ({
                        ...prev,
                        country: selectedOption ? selectedOption.value : '',
                        city: '' // Clear city when country changes
                      }));
                      if (validationErrors.country) {
                        setValidationErrors(prev => ({ ...prev, country: '' }));
                      }
                    }}
                    placeholder={t('completeProfile.fields.country.placeholder')}
                    isClearable
                    isSearchable
                    isRtl={isRTL}
                    styles={selectStyles}
                  />
                  <img
                    src="/public/icons/location.svg"
                    alt={t('completeProfile.fields.country.label')}
                    style={{
                      position: 'absolute',
                      left: isRTL ? 'auto' : '20px',
                      right: isRTL ? '20px' : 'auto',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '20px',
                      height: '20px',
                      pointerEvents: 'none',
                      zIndex: 0
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                {validationErrors.country && (
                  <p style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '14px',
                    color: '#EF4444',
                    margin: '4px 0 0 0',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {validationErrors.country}
                  </p>
                )}
              </div>

              <div style={{ flex: 1 }}>
                <label style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '10px',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('completeProfile.fields.city.label')} <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <Select
                    options={cityOptions}
                    value={cityOptions.find(opt => opt.value === formData.city)}
                    onChange={(selectedOption) => {
                      setFormData(prev => ({ ...prev, city: selectedOption ? selectedOption.value : '' }));
                      if (validationErrors.city) {
                        setValidationErrors(prev => ({ ...prev, city: '' }));
                      }
                    }}
                    placeholder={t('completeProfile.fields.city.placeholder')}
                    isClearable
                    isSearchable
                    isDisabled={!formData.country}
                    isRtl={isRTL}
                    styles={selectStyles}
                  />
                  <img
                    src="/public/icons/location.svg"
                    alt={t('completeProfile.fields.city.label')}
                    style={{
                      position: 'absolute',
                      left: isRTL ? 'auto' : '20px',
                      right: isRTL ? '20px' : 'auto',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '20px',
                      height: '20px',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                {validationErrors.city && (
                  <p style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '14px',
                    color: '#EF4444',
                    margin: '4px 0 0 0',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {validationErrors.city}
                  </p>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '40px' }}>
              <label style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: '#374151',
                display: 'block',
                marginBottom: '10px',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('completeProfile.fields.referralCode.label')}
              </label>
              <input
                type="text"
                value={formData.referralCode}
                onChange={(e) => {
                  const normalizedCode = e.target.value.toUpperCase().replace(/\s+/g, '');
                  setFormData(prev => ({ ...prev, referralCode: normalizedCode }));
                  if (validationErrors.referralCode) {
                    setValidationErrors(prev => ({ ...prev, referralCode: '' }));
                  }
                }}
                placeholder={t('completeProfile.fields.referralCode.placeholder')}
                dir="ltr"
                style={{
                  width: '100%',
                  height: '56px',
                  padding: isRTL ? '0 20px' : '0 20px',
                  border: `1.5px solid ${validationErrors.referralCode ? '#EF4444' : '#E5E7EB'}`,
                  borderRadius: '12px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '16px',
                  color: '#111827',
                  backgroundColor: '#FFFFFF',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  textAlign: isRTL ? 'right' : 'left',
                  letterSpacing: '0'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = validationErrors.referralCode ? '#EF4444' : config.primaryColor;
                  e.target.style.boxShadow = validationErrors.referralCode ? '0 0 0 3px rgba(239, 68, 68, 0.12)' : `0 0 0 3px ${config.primaryColor}1A`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = validationErrors.referralCode ? '#EF4444' : '#E5E7EB';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {validationErrors.referralCode && (
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '14px',
                  color: '#EF4444',
                  margin: '4px 0 0 0',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {validationErrors.referralCode}
                </p>
              )}
            </div>

            {/* Specialities - Only for Service Providers */}
            {userType?.toLowerCase() === 'service_provider' && (
              <div style={{ marginBottom: '40px' }}>
                <label style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '10px',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {t('completeProfile.fields.speciality.label')}
                </label>
                <div style={{ position: 'relative' }}>
                  <Select
                    isMulti
                    options={specialityOptions}
                    value={specialityOptions.filter(opt => formData.specialities.includes(opt.value))}
                    onChange={(selectedOptions) => {
                      const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                      setFormData(prev => ({ ...prev, specialities: selectedValues }));
                      if (validationErrors.specialities) {
                        setValidationErrors(prev => ({ ...prev, specialities: '' }));
                      }
                    }}
                    placeholder={t('completeProfile.fields.speciality.placeholder')}
                    isSearchable={false}
                    isRtl={isRTL}
                    styles={{
                      ...selectStyles,
                      control: (base, state) => ({
                        ...base,
                        ...selectStyles.control(base, state),
                        border: `1.5px solid ${validationErrors.specialities ? '#EF4444' : '#E5E7EB'}`,
                        minHeight: '56px',
                        boxShadow: state.isFocused ? `0 0 0 3px ${config.primaryColor}20` : 'none',
                        '&:hover': {
                          borderColor: validationErrors.specialities ? '#EF4444' : config.primaryColor
                        }
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: `${config.primaryColor}15`,
                        borderRadius: '8px'
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: config.primaryColor,
                        fontFamily: '"Red Hat Display", sans-serif',
                        fontWeight: 500
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: config.primaryColor,
                        '&:hover': {
                          backgroundColor: config.primaryColor,
                          color: 'white'
                        }
                      })
                    }}
                  />
                </div>
                {validationErrors.specialities && (
                  <p style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '14px',
                    color: '#EF4444',
                    margin: '4px 0 0 0',
                    textAlign: isRTL ? 'right' : 'left'
                  }}>
                    {validationErrors.specialities}
                  </p>
                )}
              </div>
            )}

            {/* Bio - Commented out for future use */}
            {/*
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 500,
                fontSize: '16px',
                color: '#374151',
                display: 'block',
                marginBottom: '10px'
              }}>
                Bio (Optional)
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                placeholder="Tell us about yourself..."
                rows={4}
                maxLength={500}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  border: `1.5px solid ${validationErrors.bio ? '#EF4444' : '#E5E7EB'}`,
                  borderRadius: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '16px',
                  color: '#1F2937',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  background: '#FFFFFF',
                  resize: 'vertical',
                  minHeight: '100px'
                }}
                onFocus={(e) => {
                  if (!validationErrors.bio) {
                    e.target.style.borderColor = config.primaryColor;
                  }
                }}
                onBlur={(e) => {
                  if (!validationErrors.bio) {
                    e.target.style.borderColor = '#E5E7EB';
                  }
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '4px'
              }}>
                {validationErrors.bio && (
                  <p style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '14px',
                    color: '#EF4444',
                    margin: 0
                  }}>
                    {validationErrors.bio}
                  </p>
                )}
                <p style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  color: '#9CA3AF',
                  margin: 0,
                  marginLeft: 'auto'
                }}>
                  {formData.bio.length}/500
                </p>
              </div>
            </div>
            */}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <button
                disabled={isLoading}
                style={{
                  width: '100%',
                  height: '60px',
                  background: isLoading ? 'rgba(73, 73, 73, 0.8)' : config.primaryColor,
                  borderRadius: '14px',
                  border: 'none',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 600,
                  fontSize: '20px',
                  color: '#FFFFFF',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row-reverse' : 'row'
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.target.style.background = '#494949'; // Hover color for primary button
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.target.style.background = config.primaryColor;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  }
                }}
                onClick={(e) => {
                  if (!isLoading) {
                    handleCompleteProfile();
                  }
                }}
              >
                {isLoading && (
                  <Loader2 size={16} className="animate-spin" />
                )}
                {isLoading ? t('completeProfile.buttons.saving') : t('completeProfile.buttons.save')}
              </button>
            </div>
          </div>
        </div>

        {/* Responsive styles */}
        <style>{`
              .profile-responsive-root {
                width: 100vw;
                height: 100vh;
                background: #FAFAFA;
                position: fixed;
                top: 0;
                left: 0;
                font-family: "Red Hat Display", sans-serif;
                overflow: hidden;
                display: flex;
              }
              .profile-left-panel {
                width: 50%;
                height: 100vh;
                background: #FEFEFA;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                padding: 80px 32px 32px 32px;
                box-sizing: border-box;
                position: relative;
                overflow: hidden;
              }
              .profile-right-panel {
                width: 50%;
                height: 100vh;
                background: #FFFFFF;
                box-sizing: border-box;
                position: relative;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                align-items: center;
              }
              .profile-form-content {
                width: 100%;
                max-width: 700px;
                padding: 15px 32px;
                box-sizing: border-box;
                margin-top: 180px; /* height of progress steps + spacing */
                overflow-y: auto;
                max-height: calc(100vh - 180px);
              }
              @media (max-width: 1024px) {
                .profile-responsive-root {
                  overflow: auto;
                }
                .profile-header {
                  padding: 0 40px;
                  height: 80px;
                }
                .profile-header > div {
                  width: 180px;
                  height: 70px;
                }
                .profile-left-panel {
                  display: none !important;
                  visibility: hidden !important;
                  position: absolute !important;
                  left: -9999px !important;
                  width: 0 !important;
                  height: 0 !important;
                  overflow: hidden !important;
                }
                .profile-right-panel {
                  width: 100% !important;
                  padding: 40px 24px 24px 24px;
                }
                .profile-form-content {
                  padding: 0 16px;
                  max-width: 100%;
                }
                .profile-form-content h2 {
                  font-size: 32px !important;
                }
                .profile-form-content p {
                  font-size: 16px !important;
                }
                /* Adjust progress steps for 1024px */
                .profile-progress-steps {
                  padding: 0 40px !important;
                  top: 100px !important;
                }
                .profile-progress-steps > div {
                  max-width: 90% !important;
                }
                /* Adjust name and country fields spacing */
                .profile-name-fields,
                .profile-country-fields {
                  gap: 20px !important;
                }
              }
              @media (max-width: 768px) {
                .profile-responsive-root {
                  flex-direction: column;
                  padding-top: 0;
                  overflow: auto;
                }
                .profile-header {
                  display: none !important;
                  visibility: hidden !important;
                  opacity: 0 !important;
                  height: 0 !important;
                  overflow: hidden !important;
                }
                .profile-left-panel {
                  display: none !important;
                  visibility: hidden !important;
                  position: absolute !important;
                  left: -9999px !important;
                  overflow: hidden !important;
                }
                .profile-progress-steps {
                  position: relative !important;
                  top: 0 !important;
                  margin-bottom: 24px !important;
                  padding: 0 !important;
                  width: 100% !important;
                }
                .profile-right-panel {
                  width: 100% !important;
                  height: auto !important;
                  min-height: 100vh;
                  padding: 40px 20px 40px 20px !important;
                  overflow: visible !important;
                  position: relative !important;
                  display: flex !important;
                  flex-direction: column !important;
                  align-items: center !important;
                }
                .profile-form-content {
                  position: relative !important;
                  top: 0 !important;
                  padding: 0 !important;
                  margin-top: 0 !important;
                  max-width: 100% !important;
                  width: 100% !important;
                }
                /* Adjust form title size for mobile */
                .profile-form-content h2 {
                  font-size: 24px !important;
                  margin-bottom: 8px !important;
                }
                .profile-form-content p {
                  font-size: 14px !important;
                }
                /* Profile image upload styling on mobile */
                .profile-image-upload-container {
                  margin-bottom: 32px !important;
                }
                .profile-image-upload-container > div > div {
                  background: transparent !important;
                }
                /* Stack name fields vertically on mobile */
                .profile-name-fields,
                .profile-country-fields {
                  flex-direction: column !important;
                  gap: 16px !important;
                }
              }
              @media (max-width: 480px) {
                .profile-right-panel {
                  padding: 40px 16px 32px 16px !important;
                }
                .profile-form-content {
                  width: 100% !important;
                  padding: 0 !important;
                }
                /* Further reduce font sizes */
                .profile-form-content h2 {
                  font-size: 22px !important;
                }
                .profile-form-content input,
                .profile-form-content label {
                  font-size: 14px !important;
                }
              }
            `}</style>
      </div>
    </>
  );
};

export default CompleteProfile;
